import { DatePicker, Divider, Select } from "antd";
import Title from "antd/es/typography/Title";
import dayjs, { Dayjs } from "dayjs";
import { useFormikContext } from "formik";
import React, { FC, useMemo, useState } from "react";
import { selectFiltering } from "shared/helpers";
import { useTranslation } from "react-i18next";
import { ReportItem, useGetAllMaterialsQuery } from "shared/api";
import { useGetAllCompaniesQuery } from "shared/api/suppliers/suppliersApi";
import { REPORT_DATE_TIME_FORMAT, REPORT_TIME_FORMAT } from "shared/constants";
import { useDebouncedValue } from "shared/hooks";
import { Colors } from "shared/themes";
import { ExecuteReportValues } from "shared/types";
import { ReportFormatSelect } from "./ReportFormatSelect";

interface OrderSummaryFilterProps {
  clearPdfUrl?: () => void;
  selectedReport?: ReportItem;
}

//  Consumption report: a single material plus the period it was consumed in.
//  Payload -> material_id, timestamp_start, timestamp_end
const OrderSummaryFilter: FC<OrderSummaryFilterProps> = ({
  clearPdfUrl,
  selectedReport,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values, setFieldValue } = useFormikContext<ExecuteReportValues>();

  const [materialSearch, setMaterialSearch] = useState("");
  const debouncedMaterialSearch = useDebouncedValue(materialSearch, 400);

  const { data: materials, isFetching: isFetchingMaterials } =
    useGetAllMaterialsQuery({
      limit: 200,
      name__icontains: debouncedMaterialSearch || undefined,
    });

  // const adaptedMaterials = useMemo(() => {
  //   return (
  //     materials?.results?.map((material) => ({
  //       label: material.name,
  //       value: material.id,
  //     })) || []
  //   );
  // }, [materials]);

  // const handleMaterialChange = (value: number | null) => {
  //   setFieldValue("material_id", value ?? null);

  //   if (clearPdfUrl) {
  //     clearPdfUrl();
  //   }
  // };

  const { data: companies, isLoading: isLoadingCompanies } =
    useGetAllCompaniesQuery({});

  const adaptedCompanies = useMemo(() => {
    return (
      companies?.map((company) => ({
        label: company.name,
        value: company.id,
      })) || []
    );
  }, [companies]);

  const handleDateChange = (
    field: "production_datetime_start" | "production_datetime_end",
    val: Dayjs | null
  ) => {
    setFieldValue(field, val ? val.toISOString() : null);
  };

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />

      <div className="flex flex-col gap-y-3">
        <div className="flex gap-x-2 items-center">
          <Title level={5} className="!w-[140px] !m-0">
            {t("selectCompany")}
          </Title>
          {" - "}
          <Select
            mode="multiple"
            size="small"
            className="w-[450px]"
            loading={isLoadingCompanies}
            placeholder={t("selectCompany")}
            value={values.company_ids}
            options={adaptedCompanies}
            onChange={(val) => setFieldValue("company_ids", val)}
            filterOption={(input, option) => selectFiltering(input, option)}
            maxTagCount="responsive"
          />
        </div>
        <div className="flex gap-x-3">
          <div className="flex gap-x-2">
            <Title level={5} className="!w-[140px] shrink-0 !m-0 !text-[14px]">
              {t("startDate")}
            </Title>
            {" - "}
            <DatePicker
              className="w-[200px]"
              showTime={{ format: REPORT_TIME_FORMAT }}
              format={REPORT_DATE_TIME_FORMAT}
              size="small"
              value={
                values.production_datetime_start
                  ? dayjs(values.production_datetime_start)
                  : null
              }
              onChange={(val) =>
                handleDateChange("production_datetime_start", val)
              }
              onOk={(val) => handleDateChange("production_datetime_start", val)}
            />
          </div>

          <Divider
            type="vertical"
            className="h-full"
            style={{ background: Colors.blue.primary }}
          />

          <div className="flex gap-x-2">
            <Title level={5} className="!m-0 !text-[14px]">
              {t("endDate")}
            </Title>
            {" - "}
            <DatePicker
              className="w-[200px]"
              showTime={{ format: REPORT_TIME_FORMAT }}
              format={REPORT_DATE_TIME_FORMAT}
              size="small"
              value={
                values.production_datetime_end
                  ? dayjs(values.production_datetime_end)
                  : null
              }
              onChange={(val) =>
                handleDateChange("production_datetime_end", val)
              }
              onOk={(val) => handleDateChange("production_datetime_end", val)}
            />
          </div>
        </div>

        <ReportFormatSelect
          selectedReport={selectedReport}
          clearPdfUrl={clearPdfUrl}
          titleClassName="!w-[140px] shrink-0 !m-0 !text-[14px]"
        />
      </div>

      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};

export { OrderSummaryFilter };
